'use client';

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileText, Plus, RefreshCw, Trash2, Upload } from 'lucide-react';
import type { ChangeEvent, ReactNode, RefObject } from 'react';
import { type useFormContext } from 'react-hook-form';
import { ALLOWED_EXTENSIONS } from './multi-file-validation';
import { isHistoricFileReference, type HistoricFileReference } from '@/lib/camunda/form-file-values';
import type { MultiFileDocumentTypeOption, MultiFileItem } from './multi-file-types';
import { useObjectUrlLifecycle } from './multi-file-object-url';

type SharedForm = ReturnType<typeof useFormContext>;

interface MultiFileContentProps {
  form: SharedForm;
  name: string;
  label: string;
  required: boolean;
  disabled: boolean;
  className?: string;
  items: MultiFileItem[];
  hasItems: boolean;
  hasFieldError: boolean;
  usedTypes: Set<string>;
  documentTypeOptions: readonly MultiFileDocumentTypeOption[];
  allowDuplicateTypes: boolean;
  addButtonLabel: string;
  emptyTitle: string;
  emptyHint: string;
  replaceButtonTitle: string;
  fileInputRef: RefObject<HTMLInputElement | null>;
  replaceInputRef: RefObject<HTMLInputElement | null>;
  historicDownloadErrors: Record<number, string>;
  historicDownloads: Set<number>;
  onFilesSelected: (event: ChangeEvent<HTMLInputElement>) => void;
  onReplaceFileSelected: (event: ChangeEvent<HTMLInputElement>) => void;
  onTypeChange: (index: number, tipo: string) => void;
  onRemove: (index: number) => void;
  onReplace: (index: number) => void;
  onTriggerFileInput: () => void;
  onHistoricDownload: (index: number, historicFile: HistoricFileReference) => void;
  canRemoveItem?: (item: MultiFileItem, index: number) => boolean;
  canChangeType?: (item: MultiFileItem) => boolean;
  renderAdditionalHeaders?: () => ReactNode;
  renderAdditionalCells?: (params: {
    item: MultiFileItem;
    index: number;
    disabled: boolean;
  }) => ReactNode;
}

interface MultiFileRowProps {
  item: MultiFileItem;
  index: number;
  file: File | null;
  historicFile: HistoricFileReference | null;
  fileName: string;
  metadataFilename: string | null;
  fileSizeLabel: string;
  availableOptions: readonly MultiFileDocumentTypeOption[];
  disabled: boolean;
  hasTipoError: boolean;
  historicDownloadError?: string;
  historicDownloadActive: boolean;
  replaceButtonTitle: string;
  onHistoricDownload: (index: number, historicFile: HistoricFileReference) => void;
  onTypeChange: (index: number, tipo: string) => void;
  onReplace: (index: number) => void;
  onRemove: (index: number) => void;
  canRemoveItem?: (item: MultiFileItem, index: number) => boolean;
  canChangeType?: (item: MultiFileItem) => boolean;
  renderAdditionalCells?: (params: {
    item: MultiFileItem;
    index: number;
    disabled: boolean;
  }) => ReactNode;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function MultiFileRow({
  item,
  index,
  file,
  historicFile,
  fileName,
  metadataFilename,
  fileSizeLabel,
  availableOptions,
  disabled,
  hasTipoError,
  historicDownloadError,
  historicDownloadActive,
  replaceButtonTitle,
  onHistoricDownload,
  onTypeChange,
  onReplace,
  onRemove,
  canRemoveItem,
  canChangeType,
  renderAdditionalCells,
}: MultiFileRowProps) {
  const { replaceObjectUrl } = useObjectUrlLifecycle();
  const rowKey = item.id
    ?? historicFile?.historicDetailId
    ?? (file ? `${file.name}-${file.lastModified}-${file.size}` : `${metadataFilename ?? 'item'}-${item.tipo}`);

  const downloadLocalFile = (selectedFile: File) => {
    const url = replaceObjectUrl(selectedFile);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = selectedFile.name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  return (
    <TableRow key={rowKey}>
      <TableCell>
        {file || historicFile ? (
          <button
            type="button"
            onClick={() => {
              if (file) {
                downloadLocalFile(file);
              } else if (historicFile) {
                onHistoricDownload(index, historicFile);
              }
            }}
            disabled={historicDownloadActive}
            className="flex cursor-pointer items-center gap-2 text-sm text-primary hover:underline"
            title={`Descargar ${fileName}`}
          >
            <FileText className="h-4 w-4 flex-shrink-0" />
            <span className="max-w-[250px] truncate">{fileName}</span>
            <span className="flex-shrink-0 text-xs text-muted-foreground">{fileSizeLabel}</span>
          </button>
        ) : metadataFilename ? (
          <div className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 flex-shrink-0" />
            <span className="max-w-[250px] truncate">{metadataFilename}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4 flex-shrink-0" />
            <span>Archivo no disponible</span>
          </div>
        )}
        {historicDownloadError ? (
          <p role="alert" className="mt-1 text-sm text-destructive">
            {historicDownloadError}
          </p>
        ) : null}
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          <Select
            value={item.tipo}
            onValueChange={value => onTypeChange(index, value)}
            disabled={disabled || canChangeType?.(item) === false}
          >
            <SelectTrigger
              className={`w-[220px] ${hasTipoError ? 'border-destructive ring-1 ring-destructive/20' : ''}`}
            >
              <SelectValue placeholder="Seleccionar tipo" />
            </SelectTrigger>
            <SelectContent>
              {availableOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasTipoError ? (
            <p className="text-sm text-destructive">
              Selecciona un tipo de documento
            </p>
          ) : null}
        </div>
      </TableCell>
      {renderAdditionalCells?.({ item, index, disabled })}
      {!disabled ? (
        <TableCell>
          <div className="flex items-center">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onReplace(index)}
              title={replaceButtonTitle}
              aria-label={replaceButtonTitle}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {canRemoveItem?.(item, index) !== false ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onRemove(index)}
                title="Eliminar archivo"
                aria-label="Eliminar archivo"
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            ) : null}
          </div>
        </TableCell>
      ) : null}
    </TableRow>
  );
}

export function MultiFileContent({
  form,
  name,
  label,
  required,
  disabled,
  className,
  items,
  hasItems,
  hasFieldError,
  usedTypes,
  documentTypeOptions,
  allowDuplicateTypes,
  addButtonLabel,
  emptyTitle,
  emptyHint,
  replaceButtonTitle,
  fileInputRef,
  replaceInputRef,
  historicDownloadErrors,
  historicDownloads,
  onFilesSelected,
  onReplaceFileSelected,
  onTypeChange,
  onRemove,
  onReplace,
  onTriggerFileInput,
  onHistoricDownload,
  canRemoveItem,
  canChangeType,
  renderAdditionalHeaders,
  renderAdditionalCells,
}: MultiFileContentProps) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={() => (
        <FormItem className={className}>
          <FormLabel>
            {label} {required && '*'}
          </FormLabel>
          <FormControl>
            <div>
              <Input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_EXTENSIONS}
                multiple
                className="hidden"
                onChange={onFilesSelected}
                disabled={disabled}
              />
              <Input
                ref={replaceInputRef}
                type="file"
                accept={ALLOWED_EXTENSIONS}
                className="hidden"
                onChange={onReplaceFileSelected}
                disabled={disabled}
              />

              {hasItems ? (
                <div
                  className={`space-y-3 rounded-lg border p-3 transition-colors ${hasFieldError ? 'border-destructive' : 'border-border'}`}
                >
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Archivo</TableHead>
                        <TableHead>Tipo de documento</TableHead>
                        {renderAdditionalHeaders?.()}
                        {!disabled ? <TableHead className="w-20" /> : null}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item, index) => {
                        const availableOptions = allowDuplicateTypes
                          ? documentTypeOptions
                          : documentTypeOptions.filter(
                            option => !usedTypes.has(option.value) || option.value === item.tipo,
                          );
                        const rawFile = item.file;
                        const file = rawFile instanceof File ? rawFile : null;
                        const historicFile = item.historicReference
                          ?? (isHistoricFileReference(rawFile) ? rawFile : null);
                        const metadataFilename = item.filename ?? null;
                        const fileName = file?.name || historicFile?.filename || metadataFilename || 'Archivo sin nombre';

                        return (
                          <MultiFileRow
                            key={item.id ?? (item.documentId && item.revision ? `${item.documentId}:${item.revision}` : historicFile?.historicDetailId ?? `${fileName}-${item.tipo}`)}
                            item={item}
                            index={index}
                            file={file}
                            historicFile={historicFile}
                            fileName={fileName}
                            metadataFilename={metadataFilename}
                            fileSizeLabel={file ? `(${formatFileSize(file.size)})` : ''}
                            availableOptions={availableOptions}
                            disabled={disabled}
                            hasTipoError={!item.tipo && form.formState.isSubmitted}
                            historicDownloadError={historicDownloadErrors[index]}
                            historicDownloadActive={historicDownloads.has(index)}
                            replaceButtonTitle={replaceButtonTitle}
                            onHistoricDownload={onHistoricDownload}
                            onTypeChange={onTypeChange}
                            onReplace={onReplace}
                            onRemove={onRemove}
                            canRemoveItem={canRemoveItem}
                            canChangeType={canChangeType}
                            renderAdditionalCells={renderAdditionalCells}
                          />
                        );
                      })}
                    </TableBody>
                  </Table>

                  {!disabled ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onTriggerFileInput}
                      className="gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      {addButtonLabel}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <button
                  type="button"
                  aria-label={emptyTitle}
                  onClick={onTriggerFileInput}
                  disabled={disabled}
                  className="group w-full cursor-pointer rounded-lg disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Empty
                    className={`gap-0 border-2 py-8 transition-colors md:p-8 ${hasFieldError ? 'border-destructive' : 'border-muted-foreground/25 group-hover:border-muted-foreground/50'}`}
                  >
                    <EmptyHeader className="gap-1">
                      <EmptyMedia className="mb-1">
                        <Upload className="h-8 w-8 text-muted-foreground" />
                      </EmptyMedia>
                      <EmptyTitle className="text-sm font-medium tracking-normal text-muted-foreground">
                        {emptyTitle}
                      </EmptyTitle>
                      <EmptyDescription className="text-xs/normal text-muted-foreground/70">
                        {emptyHint}
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </button>
              )}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
